import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { isAmazon } from 'helpers/shared';
import isNil from 'ramda/src/isNil';
import memoizeWith from 'ramda/src/memoizeWith';
import Select from '@fountain/ui-kit/components/select';
import Input from '@fountain/ui-kit/components/input';
import Dropdown from '@fountain/ui-kit/components/dropdown';
import Button from '@fountain/ui-kit/components/button';
import { AngleDownIcon } from 'shared/icons';
import LocationPicker from 'shared/location-picker';
import ConditionalWrapper from '../conditional-wrapper';
import FilterModal from '../filter-modal';
import { DISTANCE_OPTIONS } from './constants';
import './styles.pcss';

export default class OpeningsDirectoryIndexDspFilters extends PureComponent {
  static propTypes = {
    accountSlug: PropTypes.string.isRequired,
    apiKeys: PropTypes.shape({
      googleMaps: PropTypes.string,
      googlePlaces: PropTypes.string,
    }).isRequired,
    loadOpenings: PropTypes.func.isRequired,
    location: PropTypes.shape({
      search: PropTypes.string.isRequired,
    }).isRequired,
    params: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    preview: PropTypes.bool,
    translations: PropTypes.shape({
      view: PropTypes.string,
      cityOrZipCodePlaceholder: PropTypes.string,
      clearAllSelections: PropTypes.string,
      jobOpenings: PropTypes.string,
      location: PropTypes.string,
      positions: PropTypes.string,
      radius: PropTypes.string,
      selectPosition: PropTypes.string,
      nonePositions: PropTypes.string,
      showRemoteJobsOnly: PropTypes.string,
      locationIsRequired: PropTypes.string,
      noResultsInRadius: PropTypes.string,
      useCurrentLocation: PropTypes.string,
      hour: PropTypes.string,
    }).isRequired,
    ui: PropTypes.shape({
      isLoading: PropTypes.bool,
    }).isRequired,
    updateParams: PropTypes.func.isRequired,
    stateCodes: PropTypes.arrayOf(PropTypes.string),
    countryCodes: PropTypes.arrayOf(PropTypes.string),
    isMobile: PropTypes.bool,
    temporaryHoldParams: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    setTemporaryHoldParams: PropTypes.func.isRequired,
    toggleFilterModal: PropTypes.func.isRequired,
    filterModal: PropTypes.bool,
  }

  static defaultProps = {
    stateCodes: [],
    countryCodes: [],
    params: {},
    temporaryHoldParams: {},
    preview: false,
    isMobile: false,
    filterModal: false,
  }

  constructor(props) {
    super(props);

    this.state = {
      minPay: this.props.params.min_pay || null,
      minPaySet: !!this.props.params.min_pay || false,
    };
  }

  componentDidMount() {
    if (this.props.preview) {
      return;
    }
    this._load(this.props.params);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.isMobile !== nextProps.isMobile) {
      this._handleCloseAndClearFilters();
    }

    if (nextProps.location.search === this.props.location.search) {
      return;
    }

    this._load(nextProps.params);
  }

  _load = (params = {}) => {
    const {
      accountSlug,
      loadOpenings,
    } = this.props;

    const locationExternalId = params.location_external_id;
    loadOpenings({
      accountSlug,
      locationExternalId,
      params,
    });
  }

  _getDistanceOptions = () => DISTANCE_OPTIONS.map(distance => `${distance}mi`);

  _changeLocation = (value) => {
    const { params, updateParams, accountSlug } = this.props;

    updateParams({
      account_slug: accountSlug,
      location_external_id: null,
      lat: value && value.latitude,
      lon: value && value.longitude,
      distance: params.distance || `${DISTANCE_OPTIONS[DISTANCE_OPTIONS.length - 2]}mi`,
      page: null,
    });
  }

  _getLocation = memoizeWith(
    (lat, lng) => `${lat}-${lng}`,
    (lat, lon) => {
      if (isNil(lat) || isNil(lon)) {
        return undefined;
      }

      return {
        latitude: parseFloat(lat),
        longitude: parseFloat(lon),
      };
    }
  )

  _handleMinPayChange = (value, isValueSet) => this.setState({ minPay: value, minPaySet: isValueSet || false });

  _getPayButtonText = () => {
    const { minPay, minPaySet } = this.state;

    if (minPay && minPaySet) {
      return `$${minPay} +`;
    }

    return 'Pay';
  }

  _handleMinPayUpdate = () => {
    const { isMobile, setTemporaryHoldParams, temporaryHoldParams } = this.props;

    if (isMobile) {
      this.setState({ minPaySet: true }, () => {
        setTemporaryHoldParams({
          ...temporaryHoldParams,
          min_pay: this.state.minPay,
        }, isMobile);
      });
      return;
    }

    const { updateParams, accountSlug } = this.props;
    const { minPay } = this.state;

    this.setState({ minPaySet: true }, () => {
      updateParams({ account_slug: accountSlug, page: null, min_pay: minPay });
    });
  }

  _handleRemovePay = () => {
    const {
      isMobile,
      setTemporaryHoldParams,
      updateParams,
      accountSlug,
    } = this.props;

    if (isMobile) {
      this.setState({ minPaySet: false, minPay: null }, () => {
        setTemporaryHoldParams({ min_pay: null });
      });
      return;
    }

    this.setState({ minPaySet: false, minPay: null }, () => {
      updateParams({ account_slug: accountSlug, min_pay: null });
    });
  }

  _handleCloseAndClearFilters = () => {
    const { params, toggleFilterModal, setTemporaryHoldParams } = this.props;
    setTemporaryHoldParams({
      distance: params.distance || null,
      state_code: params.state_code || null,
      country_code: params.country_code || null,
      additional_info: params.additional_info || null,
      min_pay: params.min_pay || null,
    });

    this._handleMinPayChange(params.min_pay, !!params.min_pay);

    toggleFilterModal(false);
  }

  _handleSubmitFilters = () => {
    const {
      updateParams,
      toggleFilterModal,
      temporaryHoldParams,
      params,
      accountSlug,
    } = this.props;
    const updatedParams = {
      ...temporaryHoldParams,
      lat: params.lat,
      lon: params.lon,
    };
    updateParams(updatedParams, accountSlug);
    toggleFilterModal(false);
  }

  _getParams = () => {
    const { isMobile, params, temporaryHoldParams } = this.props;

    if (isMobile) return temporaryHoldParams;
    return params;
  }

  render() {
    const {
      accountSlug,
      apiKeys,
      stateCodes,
      countryCodes,
      params,
      translations,
      ui,
      updateParams,
      isMobile,
      filterModal,
    } = this.props;

    const filterParams = this._getParams();
    const distanceOptions = this._getDistanceOptions();

    return (
      <div className="openings-directory-index-filters__container-dsp">
        <div className="openings-directory-index-filters-dsp">
          <div className="openings-directory-index-filters-dsp__location openings-directory-index-filters__input">
            <LocationPicker
              apiKey={apiKeys.googleMaps}
              id="address"
              className="openings-directory-index-filters-dsp__location-input"
              onChange={this._changeLocation}
              disabled={ui.isLoading}
              value={this._getLocation(params.lat, params.lon)}
              translations={translations}
              requestLocationOnLoad
            />
            <img alt="Powered by Google" className="openings-directory-index-filters__powered-by-google" src="https://s3-us-west-1.amazonaws.com/static.fountain.com/powered_by_google_on_white.png" />
          </div>
          <ConditionalWrapper
            condition={isMobile}
            Wrapper={children =>
              filterModal && <FilterModal handleClose={this._handleCloseAndClearFilters} handleSubmit={this._handleSubmitFilters}>{children}</FilterModal>
            }
          >
            <div className="openings-directory-index-filters-dsp__distance openings-directory-index-filters-dsp__input">
              <Select
                classNameMenu="openings-directory-index-filters-dsp__select"
                disabled={ui.isLoading}
                fullWidth
                id="distance"
                onChange={value => updateParams({ account_slug: accountSlug, page: null, distance: value }, isMobile)}
                placeholder="Distance"
                value={filterParams.distance || `${DISTANCE_OPTIONS[DISTANCE_OPTIONS.length - 2]}mi`}
              >
                {distanceOptions.map(distance => (
                  <Select.Option key={distance} value={distance}>{distance}</Select.Option>
                ))}
              </Select>
            </div>
            <div className="openings-directory-index-filters-dsp__input">
              <Select
                classNameMenu="openings-directory-index-filters-dsp__select"
                disabled={ui.isLoading}
                fullWidth
                id="state_code"
                onChange={
                  value => updateParams(
                    {
                      account_slug: accountSlug,
                      page: null,
                      state_code: value,
                      position_id: null,
                    },
                    isMobile
                  )
                }
                placeholder="State"
                value={filterParams.state_code}
              >
                <Select.Option value={undefined}>{translations.nonePositions}</Select.Option>
                {stateCodes.map(code => (
                  <Select.Option key={code} value={code}>{code}</Select.Option>
                ))}
              </Select>
            </div>
            {isAmazon(accountSlug) && (
              <div className="openings-directory-index-filters-dsp__input">
                <Select
                  classNameMenu="openings-directory-index-filters-dsp__select"
                  disabled={ui.isLoading}
                  fullWidth
                  id="country_code"
                  onChange={
                    value => updateParams(
                      {
                        account_slug: accountSlug,
                        page: null,
                        country_code: value,
                        position_id: null,
                      },
                    isMobile
                    )
                  }
                  placeholder="Country"
                  value={filterParams.country_code}
                >
                  <Select.Option value={undefined}>{translations.nonePositions}</Select.Option>
                  {countryCodes.map(code => (
                    <Select.Option key={code} value={code}>{code}</Select.Option>
                  ))}
                </Select>
              </div>
            )}
            <div className="openings-directory-index-filters-dsp__input openings-directory-index-filters-dsp__pay">
              <Dropdown
                classNameMenu="openings-directory-index-filters-dsp__select"
                position="left"
                toggle={(
                  <div>
                    <Input
                      className="openings-directory-index-filters-dsp__readonly openings-directory-index-filters-dsp__input"
                      id="minPayDropdown"
                      value={this._getPayButtonText()}
                      readOnly
                    />
                    <AngleDownIcon
                      className="select__toggle-icon"
                    />
                  </div>
                )}
              >
                <div className="openings-directory-index-filters-dsp__dropdown-content">
                  <div className="openings-directory-index-filters-dsp__dropdown-content-inner">
                    <span>$</span>
                    <Input
                      className="openings-directory-index-filters-dsp__dropdown-content-inner-input"
                      id="minPay"
                      onChange={this._handleMinPayChange}
                      value={this.state.minPay}
                      placeholder="15.00"
                      type="number"
                    />
                    <span>/ {translations.hour} +</span>
                  </div>
                  <Button theme="secondary" size="small" onClick={() => this._handleRemovePay(isMobile)}>Remove</Button>
                  <Button size="small" onClick={this._handleMinPayUpdate}>Update</Button>
                </div>
              </Dropdown>
            </div>
            <div className="openings-directory-index-filters-dsp__input">
              <Select
                classNameMenu="openings-directory-index-filters-dsp__select"
                disabled={ui.isLoading}
                fullWidth
                id="additional_info"
                onChange={value => updateParams({ account_slug: accountSlug, page: null, additional_info: value }, isMobile)}
                placeholder="Add'l Info"
                value={filterParams.additional_info}
              >
                <Select.Option value={undefined}>{translations.nonePositions}</Select.Option>
                {['$1,000 Sign-on Bonus', '$2,000 Sign-on Bonus', '$3,000 Sign-on Bonus', 'Helper'].map(info => (
                  <Select.Option key={info} value={info}>{info}</Select.Option>
                ))}
              </Select>
            </div>
          </ConditionalWrapper>
        </div>
      </div>
    );
  }
}
